/**
 * Overnight Forecast Table — tabular format of per-property overnight predictions.
 * Sorted by risk level (at-risk first), then kWh ascending.
 * Tier 2 component when at-risk > 0, Tier 3 otherwise.
 */

import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import type { FleetSystemWithSeverity } from '@/lib/fleet-severity';

interface OvernightForecastTableProps {
  systems: FleetSystemWithSeverity[];
}

type RiskLevel = 'at_risk' | 'tight' | 'comfortable';

interface ForecastTableRow {
  id: string;
  name: string;
  kwhAtDawn: number;
  confidence: string;
  riskLevel: RiskLevel;
}

function classifyRisk(s: FleetSystemWithSeverity): RiskLevel {
  if (!s.hasOvernightForecast || s.gridMode === 'grid_primary') return 'comfortable';
  if (s.forecastRisk && s.breach_probability > 0.6) return 'at_risk';
  const sunriseKwh = s.overnightKwhAtSunrise ?? 0;
  const reserve = s.reserve_kwh ?? 0;
  if (s.forecastRisk || sunriseKwh < reserve * 1.5) return 'at_risk';
  if (sunriseKwh < reserve * 2.5) return 'tight';
  return 'comfortable';
}

const RISK_CONFIG: Record<RiskLevel, { label: string; className: string }> = {
  at_risk: { label: 'At Risk', className: 'text-status-offline font-medium' },
  tight: { label: 'Tight', className: 'text-wt-amber-primary' },
  comfortable: { label: 'Comfortable', className: 'text-status-online' },
};

const CONFIDENCE_CLASS: Record<string, string> = {
  high: 'text-status-online',
  very_high: 'text-status-online',
  medium: 'text-wt-amber-primary',
  low: 'text-wt-text-muted',
  very_low: 'text-wt-text-muted',
};

export function OvernightForecastTable({ systems }: OvernightForecastTableProps) {
  const navigate = useNavigate();
  const { setPropertyScope } = useApp();

  const rows: ForecastTableRow[] = useMemo(() => {
    return systems
      .filter((s) => s.online && s.gridMode !== 'grid_primary' && s.hasOvernightForecast)
      .map((s) => ({
        id: s.id,
        name: s.systemName,
        kwhAtDawn: s.overnightKwhAtSunrise ?? 0,
        confidence: s.confidence ?? 'unknown',
        riskLevel: classifyRisk(s),
      }))
      .sort((a, b) => {
        const order: Record<RiskLevel, number> = { at_risk: 0, tight: 1, comfortable: 2 };
        const riskDiff = order[a.riskLevel] - order[b.riskLevel];
        if (riskDiff !== 0) return riskDiff;
        return a.kwhAtDawn - b.kwhAtDawn;
      });
  }, [systems]);

  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4 text-center">
        No overnight forecasts available.
      </p>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-border/50">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-wt-bg-tertiary/50">
            <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Property</th>
            <th className="text-right px-3 py-2 text-xs font-medium text-muted-foreground">kWh at Dawn</th>
            <th className="text-center px-3 py-2 text-xs font-medium text-muted-foreground">Confidence</th>
            <th className="text-right px-3 py-2 text-xs font-medium text-muted-foreground">Risk</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const riskConfig = RISK_CONFIG[row.riskLevel];
            const confClass = CONFIDENCE_CLASS[row.confidence] ?? 'text-wt-text-muted';
            return (
              <tr
                key={row.id}
                className="border-t border-border/30 hover:bg-wt-bg-tertiary/30 cursor-pointer transition-colors"
                onClick={() => {
                  setPropertyScope(row.id, row.name);
                  navigate(`/app/property/${row.id}`);
                }}
              >
                <td className="px-3 py-2 text-foreground truncate max-w-[200px]">{row.name}</td>
                <td className="px-3 py-2 text-right font-mono text-foreground">
                  {row.kwhAtDawn.toFixed(1)}
                </td>
                <td className={cn('px-3 py-2 text-center text-xs capitalize', confClass)}>
                  {row.confidence.replace('_', ' ')}
                </td>
                <td className={cn('px-3 py-2 text-right text-xs', riskConfig.className)}>
                  {riskConfig.label}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function getAtRiskCount(systems: FleetSystemWithSeverity[]): number {
  return systems.filter(
    (s) => s.online && s.gridMode !== 'grid_primary' && classifyRisk(s) !== 'comfortable'
  ).length;
}

export function getLowestAtRisk(systems: FleetSystemWithSeverity[]): { name: string; kwh: number } | null {
  const atRisk = systems
    .filter((s) => s.online && s.gridMode !== 'grid_primary' && classifyRisk(s) !== 'comfortable')
    .sort((a, b) => (a.overnightKwhAtSunrise ?? 0) - (b.overnightKwhAtSunrise ?? 0));
  if (atRisk.length === 0) return null;
  return { name: atRisk[0].systemName, kwh: atRisk[0].overnightKwhAtSunrise ?? 0 };
}
